// The landing forge, driven only by real indexed state: the most active live launch.
//   ingots   = that launch's lots, coloured by the difficulty each proof met
//   flame    = its current difficulty (as last indexed)
//   hammer   = its estimated network hashrate (from its recent proofs; 0 when nobody is forging)
//   ingot hop= a Forged event arriving on the live stream
// With no launch forging right now, the forge is shown cold and empty.
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LOT, N_LOTS } from '@hashcurve/protocol';
import { useFeedStream } from '../../hooks/useFeedStream';
import { api } from '../../lib/api';
import type { LaunchSummary } from '../../lib/api';
import { difficultyBits, fmtBits } from '../../lib/bits';
import { formatPct, formatRate } from '../../lib/format';
import { temperColor } from '../../lib/temper';
import { useMiniStrip } from '../../components/MiniStrip';
import { ForgeScene } from '../../components/ForgeScene';
import type { ForgeEvent } from '../../components/ForgeScene';
import type { SceneInputs, SceneLot } from '../../pixel/forgeScene';
import { EMPTY_LOTS } from '../../pixel/forgeScene';
import s from './LiveForge.module.css';

/** The launch the landing forge follows: the most active one that is still forging. */
function useFeaturedLaunch() {
  return useQuery({
    queryKey: ['launches', 'featured'],
    queryFn: () => api.launches({ view: 'hot-work', limit: 8 }),
    refetchInterval: 15_000,
    select: (r) => r.items.find((l) => l.phase === 'active' && l.ignited) ?? null,
  });
}

export function LiveForge() {
  const featured = useFeaturedLaunch();
  const l = featured.data ?? null;
  return l ? <Featured key={l.address} l={l} /> : <Cold loading={featured.isPending} down={featured.isError} />;
}

function Featured({ l }: { l: LaunchSummary }) {
  const sold = BigInt(l.sold);
  const strip = useMiniStrip(l.address, sold, l.buys, l.sells);
  const [forge, setForge] = useState<ForgeEvent | null>(null);
  useFeedStream({
    launch: l.address,
    onItem: (i) => {
      if (i.type !== 'forge' || !i.sold) return;
      const lot = Number((BigInt(i.sold) - 1n) / LOT);
      setForge({ key: i.id, lot, color: i.difficultyMetFloat ? temperColor(i.difficultyMetFloat) : '#FFC247' });
    },
  });

  const lots: readonly SceneLot[] = strip
    ? strip.cells.map((c) => ({ fill: c.fill, color: c.fill > 0 && c.difficulty !== null ? temperColor(c.difficulty) : null }))
    : EMPTY_LOTS;
  const next = Number(sold / LOT);
  const busy = l.forges10m > 0 && l.estNetworkHashrateFloat !== null;
  const inputs: SceneInputs = {
    mode: 'active',
    difficulty: l.difficultyFloat,
    decay: 0,
    hashrate: busy ? l.estNetworkHashrateFloat! : 0,
    holding: false,
    lots,
    nextLot: next < N_LOTS ? next : -1,
  };
  const bits = l.difficultyFloat ? fmtBits(difficultyBits(l.difficultyFloat)) : '—';

  return (
    <div className={s.wrap}>
      <ForgeScene inputs={inputs} forge={forge} height={112} mobileHeight={104} scale={4} mobileScale={2} label={`Live forge of $${l.symbol}: ${l.lotsForged} of ${N_LOTS} lots forged, difficulty ${bits} bits.`} testId="hero-scene">
        <Link to={`/t/${l.address}`} className={`chip ${s.chip}`} data-tone="live" data-testid="hero-live-label">
          ${l.symbol} · live
        </Link>
      </ForgeScene>
      <div className={s.hud}>
        <dl className={s.facts}>
          <div>
            <dt>Lots forged</dt>
            <dd>
              {l.lotsForged}/{N_LOTS}
            </dd>
          </div>
          <div>
            <dt>Difficulty</dt>
            <dd>{bits} bits</dd>
          </div>
          <div>
            <dt>Curve</dt>
            <dd>{formatPct(l.progress, 1)}</dd>
          </div>
          <div>
            <dt>Est. network hashrate</dt>
            <dd>{l.estNetworkHashrateFloat ? formatRate(l.estNetworkHashrateFloat) : '—'}</dd>
          </div>
        </dl>
        <Link className="btn btn-primary" to={`/t/${l.address}`} data-testid="hero-open">
          Mine ${l.symbol} →
        </Link>
      </div>
    </div>
  );
}

function Cold({ loading, down }: { loading: boolean; down: boolean }) {
  const inputs: SceneInputs = { mode: 'cold', difficulty: null, decay: 0, hashrate: 0, holding: false, lots: EMPTY_LOTS, nextLot: -1 };
  const text = down ? 'The indexer is not reachable, so no live forge can be shown.' : loading ? 'Looking for a live forge…' : 'No launch is forging right now. The furnace is cold.';
  return (
    <div className={s.wrap}>
      <ForgeScene inputs={inputs} height={112} mobileHeight={104} scale={4} mobileScale={2} label={`Cold forge. ${text}`} testId="hero-scene" />
      <div className={s.hud}>
        <p className={s.caption}>{text}</p>
        <Link className="btn btn-primary" to="/create" data-testid="hero-create">
          Launch a token →
        </Link>
      </div>
    </div>
  );
}
